/*
 * Awhi — custom cart drawer (Phase 3).
 *
 * Intercepts the Flow theme's "wetheme-toggle-right-drawer" CustomEvent
 * for type "cart" with a capture-phase listener on `document`, so the
 * stock Vue drawer never receives cart opens (search / quick-shop /
 * size-guide drawers are untouched). Cart state comes from /cart.js;
 * recommendations from native Search & Discovery.
 */
(function () {
  'use strict';

  var TOGGLE_EVENT = 'wetheme-toggle-right-drawer';
  var UPDATE_EVENT = 'wetheme-cart-update';
  // The seller-instructions attribute. This is the store's ORIGINAL key
  // (pre-08/08): it fed the "Special instructions for seller" box, lands under
  // Additional details in admin, and their orders-CSV Note Attributes column
  // has always carried it. Do not rename it; exports and any admin process
  // keyed on it would silently stop matching. From 08/08 to 19/08 it briefly
  // held the gift message instead (the duplication the client reported);
  // syncGiftNote() self-heals that case.
  var GIFT_NOTE_KEY = 'gift_note';

  var root, config, moneyFormat;
  var cart = null;
  var upsellProducts = null;
  var giftNoteTimer = null;
  var orderNoteTimer = null;
  // Dirty = typed since the last successful save. The focus check alone is not
  // enough: click a quantity button mid-typing and focus moves, render() runs,
  // and syncGiftNote() would overwrite the box with the stale server value,
  // which the pending debounce would then save. Dirty stays true until the
  // server confirms the save, so a failed save (429) also keeps the text.
  var giftNoteDirty = false;
  var orderNoteDirty = false;
  // Guards for the legacy-duplicate self-heal in syncGiftNote():
  // selfHealed = one-shot per page; healArmed = disarmed the moment the
  // customer types in either box this page, so identical text typed THIS
  // session can never be mistaken for the 14/08 duplicate; healedValue lets
  // sync ignore a stale cart snapshot that races the heal and would
  // otherwise resurrect the cleared text. The whole heal is also date-boxed
  // (healWindowOpen): carts from the 14/08-19/08 duplication expire within a
  // fortnight, so after 06/09/2026 the heal is dead code and byte-identical
  // text in both boxes is always treated as genuine.
  var selfHealed = false;
  var healArmed = true;
  var healedValue = null;
  function healWindowOpen() {
    var d = new Date();
    var n = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    return n <= 20260906;
  }
  var suppressUpdateEvent = false;
  var lastFocused = null;
  // Set when a line change is refused (stock limit) so the reason can be
  // printed against that line on the next render. Cleared on the next
  // successful change and whenever the drawer opens.
  var lineError = null;

  function $(sel) {
    return root.querySelector(sel);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var format = moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function resizeImage(url, size) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + size + '&height=' + size + '&crop=center';
  }

  /* ------------------------------------------------ open / close */

  function isOpen() {
    return root.classList.contains('is-open');
  }

  function open() {
    lastFocused = document.activeElement;
    lineError = null;
    root.hidden = false;
    // Next frame so the transition runs from the hidden state.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
    });
    document.body.classList.add('awhi-cd-open');
    refresh().then(function () {
      var close = $('.awhi-cd__close');
      if (close) close.focus();
    });
    if (config.upsell) loadUpsell();
  }

  function close() {
    root.classList.remove('is-open');
    document.body.classList.remove('awhi-cd-open');
    window.setTimeout(function () {
      if (!isOpen()) root.hidden = true;
    }, 320);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onToggle(e) {
    var detail = e.detail || {};
    var type = detail.type || 'cart';
    if (type !== 'cart') return;
    // Swallow cart opens even before init: letting the stock Vue drawer take
    // one is worse than missing a click, because its cart panel contains a
    // form whose empty name="note" blanks the customer's gift note on submit,
    // and its own gift box re-writes the gift_note attribute. A pre-init
    // click simply does nothing; the customer's next click opens the drawer.
    e.stopPropagation();
    if (!root) return;
    if (detail.forceOpen === false) {
      close();
    } else if (detail.forceOpen) {
      if (!isOpen()) open();
      else refresh();
    } else if (isOpen()) {
      close();
    } else {
      open();
    }
  }

  function onCartUpdate() {
    if (suppressUpdateEvent) return;
    if (isOpen()) refresh();
  }

  function announceCart(c) {
    suppressUpdateEvent = true;
    document.documentElement.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, { detail: { cart: c } })
    );
    suppressUpdateEvent = false;
  }

  /* ------------------------------------------------ cart state */

  // Resolves to a cart, or to null when the storefront answers with
  // anything that is not one — it rate-limits with a 429 HTML page, and a
  // rejected promise here used to strand the caller mid-update.
  function fetchCart() {
    return window
      .fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        try {
          var c = JSON.parse(text);
          return c && c.items ? c : null;
        } catch (err) {
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  }

  function refresh() {
    return fetchCart().then(function (c) {
      // Never replace a good cart with a failed read.
      if (c) cart = c;
      render();
      return c;
    });
  }

  function changeLine(key, quantity, row) {
    if (row) row.classList.add('is-loading');
    return window
      .fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
      .then(function (r) {
        // Read as text and parse defensively. Asking for more than the
        // available stock answers 422, and that body is an error object
        // ({status, message, description}) — never a cart. Assigning it to
        // `cart` used to blow up renderItems(), which left the row wearing
        // .is-loading (pointer-events: none) and killed BOTH quantity
        // buttons on that line until the drawer was reopened.
        return r.text().then(function (text) {
          var body = null;
          try {
            body = JSON.parse(text);
          } catch (err) {
            body = null;
          }
          return { ok: r.ok, body: body };
        });
      })
      .then(function (res) {
        if (!res.ok || !res.body || !res.body.items) {
          lineError = {
            key: key,
            message:
              (res.body && (res.body.description || res.body.message)) ||
              'Sorry, we could not update that quantity.'
          };
          // Re-sync where possible, but render regardless: refresh() can come
          // back empty when the storefront is rate-limiting, and the row must
          // still be redrawn from the last known good cart. .is-loading
          // carries pointer-events: none, so if it outlives the request it
          // kills every control on that line.
          return refresh().then(function () {
            clearLoading(row);
            return cart;
          });
        }
        lineError = null;
        cart = res.body;
        render();
        announceCart(cart);
        if (config.upsell) renderUpsell();
        return cart;
      })
      .catch(function () {
        lineError = { key: key, message: 'Sorry, we could not update that quantity.' };
        clearLoading(row);
        render();
        return cart;
      });
  }

  // Belt and braces: render() rebuilds the item list and drops the class with
  // it, but if there is no cart to render from, the row still must not be
  // left inert.
  function clearLoading(row) {
    if (row && row.classList) row.classList.remove('is-loading');
  }

  // keepalive lets the request survive a navigation, so a save flushed on the
  // checkout click still lands even though the page is being torn down.
  function updateCart(payload) {
    return window.fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  // Gift note goes to cart.note ONLY. order.note is what the packing slip
  // prints; writing the attribute as well is what duplicated every gift note
  // into Additional details (client, 19/08). /cart/update.js verified 19/08:
  // a note-only payload leaves the attribute hash completely untouched, and
  // attribute writes merge per key rather than replacing the hash.
  //
  // ONE save in flight per field, latest value queued behind it (review
  // 19/08): the blur and checkout flushes made two concurrent saves of the
  // same field possible, and then whichever response returned first cleared
  // the dirty flag while newer text was unsaved, and the server could apply
  // the two writes in inverted order so the stale text won on the order.
  // Serialising per field removes both, and the dirty flag only clears when
  // the response is for the latest value AND the box still holds it.
  var giftInFlight = false;
  var giftQueued = null;
  function saveGiftNote(value) {
    if (giftInFlight) {
      giftQueued = value;
      return;
    }
    giftInFlight = true;
    updateCart({ note: value }).then(
      function (r) {
        giftInFlight = false;
        if (r && r.ok) {
          if (cart) cart.note = value;
          var input = $('[data-awhi-cd-giftnote]');
          if (giftQueued === null && (!input || input.value === value)) giftNoteDirty = false;
        }
        if (giftQueued !== null) {
          var q = giftQueued;
          giftQueued = null;
          saveGiftNote(q);
        }
      },
      function () {
        // Failed save: dirty stays true, the typed text is preserved against
        // renders, and the next input/blur/flush retries.
        giftInFlight = false;
        if (giftQueued !== null) {
          var q = giftQueued;
          giftQueued = null;
          saveGiftNote(q);
        }
      }
    );
  }

  // The second box: seller instructions (packaging, delivery). Attribute, not
  // note, so it lands under Additional details and stays out of the gift
  // message. An empty value removes the key entirely (verified 19/08).
  var orderInFlight = false;
  var orderQueued = null;
  function saveOrderNote(value) {
    if (orderInFlight) {
      orderQueued = value;
      return;
    }
    orderInFlight = true;
    var attributes = {};
    attributes[GIFT_NOTE_KEY] = value;
    updateCart({ attributes: attributes }).then(
      function (r) {
        orderInFlight = false;
        if (r && r.ok) {
          if (cart && cart.attributes) cart.attributes[GIFT_NOTE_KEY] = value;
          var extra = $('[data-awhi-cd-ordernote]');
          if (orderQueued === null && (!extra || extra.value === value)) orderNoteDirty = false;
        }
        if (orderQueued !== null) {
          var q = orderQueued;
          orderQueued = null;
          saveOrderNote(q);
        }
      },
      function () {
        orderInFlight = false;
        if (orderQueued !== null) {
          var q = orderQueued;
          orderQueued = null;
          saveOrderNote(q);
        }
      }
    );
  }

  /* ------------------------------------------------ rendering */

  function render() {
    // `cart.items` guard, not just `cart`: anything that is not a real cart
    // payload must not be allowed to drive the UI.
    if (!cart || !cart.items) return;

    var count = $('[data-awhi-cd-count]');
    if (count) count.textContent = cart.item_count;

    var hasItems = cart.item_count > 0;
    var empty = $('[data-awhi-cd-empty]');
    if (empty) empty.hidden = hasItems;
    var footer = $('[data-awhi-cd-footer]');
    if (footer) footer.hidden = !hasItems;
    // Gift note is gated on the greeting card being in the cart when a card
    // product is named (Tasmyn 30/07 — they hand-write notes on the packing
    // slip today and want the note to belong to a card purchase). With no
    // card product set it behaves as before: visible whenever the cart has
    // items.
    var giftWrap = $('[data-awhi-cd-giftnote-wrap]');
    if (giftWrap) {
      var giftIds = config.giftNoteIds && config.giftNoteIds.length ? config.giftNoteIds : null;
      var gated = !!(config.giftNoteCardId || giftIds);
      var cardOk = true;
      if (gated) {
        // Either gate satisfies it: the single named card product, or any
        // product from the gift collection. Neither set means no gate at all
        // and the note behaves as it did before the collection was added.
        cardOk = (cart && cart.items ? cart.items : []).some(function (item) {
          if (config.giftNoteCardId && item.product_id === config.giftNoteCardId) return true;
          return !!giftIds && giftIds.indexOf(item.product_id) !== -1;
        });
      }
      giftWrap.hidden = !hasItems || !cardOk;
    }
    // The instructions box is deliberately NOT gated on the gift collection.
    // Packaging and delivery requests apply to any order.
    var orderNoteWrap = $('[data-awhi-cd-ordernote-wrap]');
    if (orderNoteWrap) orderNoteWrap.hidden = !hasItems;
    // When both boxes show they read as one band: the first drops its divider
    // and the second its top padding. Class-toggled here rather than a :has()
    // selector so older browsers get the same rendering.
    var bothVisible = !!(giftWrap && !giftWrap.hidden && orderNoteWrap && !orderNoteWrap.hidden);
    if (giftWrap) giftWrap.classList.toggle('awhi-cd__giftnote--joined-top', bothVisible);
    if (orderNoteWrap) orderNoteWrap.classList.toggle('awhi-cd__giftnote--joined-bottom', bothVisible);
    var upsellWrap = $('[data-awhi-cd-upsell]');
    if (upsellWrap && !hasItems) upsellWrap.hidden = true;

    renderShipBar(hasItems);
    renderItems();
    renderFooter();
    syncGiftNote();
    if (upsellWrap && hasItems && upsellProducts) renderUpsell();
  }

  function renderShipBar(hasItems) {
    var bar = $('[data-awhi-cd-shipbar]');
    if (!bar) return;
    if (!config.threshold || !hasItems) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    var total = cart.total_price;
    var pct = Math.min((total / config.threshold) * 100, 100);
    $('[data-awhi-cd-shipfill]').style.width = pct + '%';
    var msg = $('[data-awhi-cd-shipmsg]');
    if (total >= config.threshold) {
      msg.innerHTML = escapeHtml(config.msgUnlocked).replace(
        /FREE SHIPPING!?/i,
        function (m) {
          return '<strong>' + m + '</strong>';
        }
      );
    } else {
      msg.textContent = config.msgRemaining.replace(
        '{amount}',
        formatMoney(config.threshold - total)
      );
    }
  }

  function renderItems() {
    var wrap = $('[data-awhi-cd-items]');
    if (!wrap) return;
    var html = '';

    cart.items.forEach(function (item) {
      var props = '';
      if (item.properties) {
        Object.keys(item.properties).forEach(function (key) {
          var v = item.properties[key];
          if (key.charAt(0) === '_' || !v) return;
          props +=
            '<p class="awhi-cd__item-variant">' +
            escapeHtml(key) + ': ' + escapeHtml(v) +
            '</p>';
        });
      }

      var priceHtml = '';
      var linePrice = item.final_line_price != null ? item.final_line_price : item.line_price;
      if (item.original_line_price > linePrice) {
        priceHtml = '<s>' + formatMoney(item.original_line_price) + '</s>' + formatMoney(linePrice);
      } else {
        priceHtml = formatMoney(linePrice);
      }

      var errorHtml =
        lineError && lineError.key === item.key
          ? '<p class="awhi-cd__item-error" role="status">' + escapeHtml(lineError.message) + '</p>'
          : '';

      html +=
        '<div class="awhi-cd__item" data-key="' + escapeHtml(item.key) + '">' +
          '<button type="button" class="awhi-cd__item-remove" data-qty-remove aria-label="Remove ' + escapeHtml(item.product_title) + ' from cart">' +
            '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
          '</button>' +
          '<a class="awhi-cd__item-thumb" href="' + escapeHtml(item.url) + '">' +
            (item.image ? '<img src="' + escapeHtml(resizeImage(item.image, 180)) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">' : '') +
          '</a>' +
          '<div class="awhi-cd__item-deets">' +
            '<div>' +
              '<p class="awhi-cd__item-name"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a></p>' +
              (item.variant_title && item.variant_title !== 'Default Title'
                ? '<p class="awhi-cd__item-variant">' + escapeHtml(item.variant_title) + '</p>'
                : '') +
              props +
            '</div>' +
            '<div class="awhi-cd__item-row">' +
              '<span class="awhi-cd__qty">' +
                '<button type="button" data-qty-minus aria-label="Decrease quantity">' +
                  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M1 6h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
                '</button>' +
                '<span class="awhi-cd__qty-num">' + item.quantity + '</span>' +
                '<button type="button" data-qty-plus aria-label="Increase quantity">' +
                  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M1 6h10M6 1v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
                '</button>' +
              '</span>' +
              '<span class="awhi-cd__item-price">' + priceHtml + '</span>' +
            '</div>' +
            errorHtml +
          '</div>' +
        '</div>';
    });

    wrap.innerHTML = html;
  }

  function renderFooter() {
    var label = $('[data-awhi-cd-subtotal-label]');
    var amount = $('[data-awhi-cd-subtotal]');
    if (!label || !amount) return;
    label.textContent =
      'Subtotal (' + cart.item_count + (cart.item_count === 1 ? ' item)' : ' items)');
    amount.textContent = formatMoney(cart.total_price);
  }

  function syncGiftNote() {
    if (!cart) return;

    // Self-heal the 14/08 duplicate: that build wrote the identical text to
    // BOTH note and the attribute. Equality alone is NOT proof (a customer
    // could type the same text in both boxes), so the heal only fires before
    // any typing this page (healArmed), once per page, and only inside the
    // date window while pre-19/08 carts can still exist.
    var attr = cart.attributes ? cart.attributes[GIFT_NOTE_KEY] : null;
    if (!selfHealed && healArmed && healWindowOpen() && attr && cart.note && attr === cart.note && !orderNoteDirty) {
      selfHealed = true;
      healedValue = attr;
      saveOrderNote('');
      attr = '';
    }
    // A cart snapshot the server serialised before the heal can arrive after
    // it; without this, that stale copy would repopulate the box and an edit
    // would persist the duplicate back.
    if (selfHealed && attr && attr === healedValue) attr = '';

    // Each field mirrors its own store and nothing else. No cross-fallback:
    // falling back from an empty note to the attribute is what made deleted
    // gift notes resurrect on pre-19/08 carts.
    var input = $('[data-awhi-cd-giftnote]');
    if (input && document.activeElement !== input && !giftNoteDirty) {
      input.value = cart.note || '';
    }

    var extra = $('[data-awhi-cd-ordernote]');
    if (extra && document.activeElement !== extra && !orderNoteDirty) {
      extra.value = attr || '';
    }
  }

  /* ------------------------------------------------ upsell */

  function loadUpsell() {
    // Curated add-ons win over recommendations when the section names an
    // add-ons collection (Tasmyn 30/07 — she wants a fixed bonus rail, not a
    // "related products" guess). Server-rendered, so no fetch at all.
    if (config.upsellFixed && config.upsellFixed.length) {
      upsellProducts = config.upsellFixed;
      renderUpsell();
      return;
    }
    if (!cart || !cart.items || !cart.items.length) {
      // Wait for refresh() to land; renderUpsell is called from render().
      fetchCart().then(function (c) {
        cart = c;
        if (c.items.length) loadUpsell();
      });
      return;
    }
    var productId = cart.items[0].product_id;
    window
      .fetch('/recommendations/products.json?product_id=' + productId + '&limit=8&intent=related', {
        credentials: 'same-origin'
      })
      .then(function (r) {
        return r.ok ? r.json() : { products: [] };
      })
      .then(function (data) {
        upsellProducts = data.products || [];
        renderUpsell();
      })
      .catch(function () {
        upsellProducts = [];
      });
  }

  function upsellCandidates() {
    if (!upsellProducts || !cart) return [];
    var inCart = {};
    cart.items.forEach(function (item) {
      inCart[item.product_id] = true;
    });
    return upsellProducts
      .filter(function (p) {
        return p.available && !inCart[p.id];
      })
      .slice(0, config.upsellMax);
  }

  function renderUpsell() {
    var wrap = $('[data-awhi-cd-upsell]');
    if (!wrap) return;
    var products = upsellCandidates();
    if (!products.length || !cart.item_count) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;

    var track = $('[data-awhi-cd-upsell-track]');
    var html = '';
    products.forEach(function (p) {
      var variant = null;
      for (var i = 0; i < (p.variants || []).length; i++) {
        if (p.variants[i].available) {
          variant = p.variants[i];
          break;
        }
      }
      if (!variant) return;
      var img = p.featured_image || (p.images && p.images[0]);
      var priceHtml =
        p.compare_at_price && p.compare_at_price > p.price
          ? '<s>' + formatMoney(p.compare_at_price) + '</s><span>' + formatMoney(p.price) + '</span>'
          : '<span>' + formatMoney(p.price) + '</span>';

      html +=
        '<div class="awhi-cd__upsell-slide">' +
          '<a class="awhi-cd__upsell-img" href="' + escapeHtml(p.url) + '">' +
            (img ? '<img src="' + escapeHtml(resizeImage(img, 180)) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
          '</a>' +
          '<div class="awhi-cd__upsell-deets">' +
            '<p class="awhi-cd__upsell-name">' + escapeHtml(p.title) + '</p>' +
            '<div class="awhi-cd__upsell-row">' +
              '<span class="awhi-cd__upsell-price">' + priceHtml + '</span>' +
              '<button type="button" class="awhi-cd__upsell-add" data-variant-id="' + variant.id + '">+ Add</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });
    track.innerHTML = html;
    track.scrollLeft = 0;

    renderUpsellDots(products.length, 0);
  }

  function renderUpsellDots(total, active) {
    var dots = $('[data-awhi-cd-upsell-dots]');
    var counter = $('[data-awhi-cd-upsell-counter]');
    if (counter) counter.textContent = (active + 1) + '/' + total;
    if (!dots) return;
    var html = '';
    for (var i = 0; i < total; i++) {
      html +=
        '<button type="button" class="awhi-cd__upsell-dot' +
        (i === active ? ' is-active' : '') +
        '" data-dot-index="' + i + '" aria-label="Recommendation ' + (i + 1) + '"></button>';
    }
    dots.innerHTML = html;
  }

  function currentUpsellIndex() {
    var track = $('[data-awhi-cd-upsell-track]');
    if (!track || !track.clientWidth) return 0;
    return Math.round(track.scrollLeft / track.clientWidth);
  }

  function addUpsell(variantId, button) {
    button.disabled = true;
    window
      .fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        return refresh();
      })
      .then(function (c) {
        announceCart(c);
      })
      .catch(function () {
        button.disabled = false;
      });
  }

  /* ------------------------------------------------ events */

  function bindEvents() {
    root.addEventListener('click', function (e) {
      var closer = e.target.closest('[data-awhi-cd-close]');
      if (closer) {
        close();
        return;
      }

      var remove = e.target.closest('[data-qty-remove]');
      if (remove) {
        var removeRow = e.target.closest('.awhi-cd__item');
        changeLine(removeRow.getAttribute('data-key'), 0, removeRow);
        return;
      }

      var minus = e.target.closest('[data-qty-minus]');
      var plus = e.target.closest('[data-qty-plus]');
      if (minus || plus) {
        var row = e.target.closest('.awhi-cd__item');
        var key = row.getAttribute('data-key');
        var item = null;
        if (!cart || !cart.items) return;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) item = cart.items[i];
        }
        if (item) {
          changeLine(key, item.quantity + (plus ? 1 : -1), row);
        }
        return;
      }

      var add = e.target.closest('.awhi-cd__upsell-add');
      if (add) {
        addUpsell(add.getAttribute('data-variant-id'), add);
        return;
      }

      var dot = e.target.closest('[data-dot-index]');
      if (dot) {
        var track = $('[data-awhi-cd-upsell-track]');
        track.scrollTo({
          left: track.clientWidth * Number(dot.getAttribute('data-dot-index')),
          behavior: 'smooth'
        });
      }
    });

    // Debounced save while typing, immediate save on blur, and a flush when
    // the checkout link is clicked: the 800ms timer alone loses the note when
    // a customer types and clicks Secure checkout inside the window, because
    // the navigation discards the pending timer. keepalive on the fetch keeps
    // the flushed request alive through the navigation.
    function flushGiftNote() {
      if (giftNoteTimer) {
        window.clearTimeout(giftNoteTimer);
        giftNoteTimer = null;
      }
      var input = $('[data-awhi-cd-giftnote]');
      if (input && giftNoteDirty) saveGiftNote(input.value);
    }

    function flushOrderNote() {
      if (orderNoteTimer) {
        window.clearTimeout(orderNoteTimer);
        orderNoteTimer = null;
      }
      var extra = $('[data-awhi-cd-ordernote]');
      if (extra && orderNoteDirty) saveOrderNote(extra.value);
    }

    var giftInput = $('[data-awhi-cd-giftnote]');
    if (giftInput) {
      giftInput.addEventListener('input', function () {
        giftNoteDirty = true;
        healArmed = false;
        window.clearTimeout(giftNoteTimer);
        giftNoteTimer = window.setTimeout(function () {
          giftNoteTimer = null;
          saveGiftNote(giftInput.value);
        }, 800);
      });
      giftInput.addEventListener('blur', flushGiftNote);
    }

    var orderNoteInput = $('[data-awhi-cd-ordernote]');
    if (orderNoteInput) {
      orderNoteInput.addEventListener('input', function () {
        orderNoteDirty = true;
        healArmed = false;
        window.clearTimeout(orderNoteTimer);
        orderNoteTimer = window.setTimeout(function () {
          orderNoteTimer = null;
          saveOrderNote(orderNoteInput.value);
        }, 800);
      });
      orderNoteInput.addEventListener('blur', flushOrderNote);
    }

    var checkoutLink = $('[data-awhi-cd-checkout]');
    if (checkoutLink) {
      checkoutLink.addEventListener('click', function () {
        flushGiftNote();
        flushOrderNote();
      });
    }

    var track = $('[data-awhi-cd-upsell-track]');
    if (track) {
      var scrollTimer = null;
      track.addEventListener('scroll', function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () {
          var total = track.children.length;
          if (total) renderUpsellDots(total, Math.min(currentUpsellIndex(), total - 1));
        }, 80);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });
  }

  /* ------------------------------------------------ init */

  function init() {
    root = document.getElementById('AwhiCartDrawer');
    if (!root) return;

    var configEl = document.getElementById('AwhiCartDrawerConfig');
    config = configEl ? JSON.parse(configEl.textContent) : { threshold: 0 };

    var globalEl = document.getElementById('wetheme-global');
    if (globalEl) {
      try {
        moneyFormat = JSON.parse(globalEl.textContent).moneyFormat;
      } catch (err) {
        moneyFormat = null;
      }
    }

    bindEvents();

    // In the theme editor, open the drawer when its section is selected.
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.querySelector('#AwhiCartDrawer')) open();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.querySelector('#AwhiCartDrawer')) close();
    });
  }

  // Capture-phase listeners on `document` fire before the Vue drawer's
  // target-phase listeners on `documentElement`, whatever the load order.
  document.addEventListener(TOGGLE_EVENT, onToggle, true);
  document.addEventListener(UPDATE_EVENT, onCartUpdate, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
